import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, ArrowsClockwise, Flag, Calendar, GlobeHemisphereWest, Horse, Sparkle } from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import CzechiaWatch from "../components/CzechiaWatch";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { DEFAULT_MD_PATH, DEFAULT_ADMIN_MD_PATH, ACTIVE_FROM_MD } from "../lib/wcRounds";
import {
    getWcOverview, getWcGroups, getWcMatches, syncWorldCup,
} from "../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_LABEL = {
    GROUP_STAGE: "Group Stage",
    LAST_32: "Round of 32",
    LAST_16: "Round of 16",
    QUARTER_FINALS: "Quarter-finals",
    SEMI_FINALS: "Semi-finals",
    THIRD_PLACE: "3rd Place",
    FINAL: "Final",
};

function fmtKick(iso) {
    if (!iso) return "TBD";
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
        weekday: "short", day: "numeric", month: "short",
        hour: "2-digit", minute: "2-digit",
    });
}

function GroupCard({ group }) {
    const label = (group.group || "").replace("GROUP_", "Group ");
    return (
        <div className="border border-white/10 bg-[#0d0d0d] p-3" data-testid={`wc-group-${group.group}`}>
            <div className="font-mono text-[10px] uppercase tracking-widest text-fa-yellow mb-1.5">
                {label}
            </div>
            <ul className="space-y-1">
                {group.teams.map((t) => (
                    <li key={t.fd_team_id} className="flex items-center justify-between gap-2 font-mono text-[11px]">
                        <Link
                            to={`/worldcup/team/${t.fd_team_id}`}
                            className="text-white hover:text-fa-yellow truncate"
                        >
                            <span className="text-white/40 mr-1.5 tabular-nums">{t.tla || "—"}</span>
                            {t.short_name || t.name}
                        </Link>
                        {t.squad_size ? (
                            <span className="text-white/30 text-[9px]">{t.squad_size}</span>
                        ) : null}
                    </li>
                ))}
            </ul>
        </div>
    );
}

function NextMatchRow({ match }) {
    const stageBadge = STAGE_LABEL[match.stage] || match.stage;
    return (
        <div className="border-l-2 border-fa-violet/40 pl-3 py-1.5" data-testid={`wc-next-${match.fd_match_id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="font-display font-black text-sm">
                    <span className="text-white">{match.home_team_name}</span>
                    <span className="text-white/30 mx-2">v</span>
                    <span className="text-white">{match.away_team_name}</span>
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 flex items-center gap-2">
                    <Calendar size={10} weight="fill" />
                    {fmtKick(match.utc_date)}
                </div>
            </div>
            <div className="font-mono text-[9px] uppercase tracking-widest text-white/30 mt-0.5">
                {stageBadge}{match.group ? ` · ${match.group.replace("GROUP_", "Group ")}` : ""}
                {match.venue ? ` · ${match.venue}` : ""}
            </div>
        </div>
    );
}

function computeSyncedAtLabel(syncedAt) {
    if (!syncedAt) return null;
    const d = new Date(syncedAt);
    const mins = Math.round((Date.now() - d.getTime()) / 60000);
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.round(hrs / 24)}d ago`;
}

// ---- Dark Horse Index widget -----------------------------------------------

function DarkHorseIndex() {
    const [horses, setHorses] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios.get(`${API}/worldcup/dark-horses?limit=5`)
            .then((r) => setHorses(r.data.horses || []))
            .catch(() => { /* silent — widget is optional */ })
            .finally(() => setLoading(false));
    }, []);

    if (loading || horses.length === 0) return null;
    const topScore = horses[0]?.total_score || 1;

    return (
        <section data-testid="wc-dark-horse-index">
            <div className="flex items-baseline justify-between mb-3">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-fa-violet flex items-center gap-1.5">
                        <Horse size={11} weight="fill" />
                        Dark Horse Index · who could be this year&apos;s Morocco?
                    </div>
                    <h2 className="font-display font-black text-xl md:text-2xl uppercase tracking-tight">
                        Top {horses.length} surprise contenders by shock-potential score
                    </h2>
                </div>
                <Link
                    to="/worldcup/dark-horses"
                    className="font-mono text-[10px] uppercase tracking-widest text-fa-violet hover:text-fa-violet/80 border border-fa-violet/40 px-3 py-1.5"
                    data-testid="wc-dh-view-all"
                >
                    View all →
                </Link>
            </div>
            <div className="border border-fa-violet/30 bg-[linear-gradient(135deg,#14102a,#0d0d0d)] p-3 space-y-2">
                {horses.map((h, idx) => {
                    const barPct = Math.max(8, Math.round((h.total_score / topScore) * 100));
                    const tla = h.team_tla || (h.team_name || "").slice(0, 3).toUpperCase();
                    return (
                        <Link
                            key={h.team_id}
                            to={`/worldcup/team/${h.team_id}`}
                            className="block border-l-2 border-fa-violet/60 pl-3 py-2 hover:bg-white/[0.03] transition-colors"
                            data-testid={`wc-dh-row-${h.team_id}`}
                        >
                            <div className="flex items-center gap-3 flex-wrap">
                                <span className="font-mono text-[10px] tabular-nums text-fa-violet w-5">#{idx + 1}</span>
                                <span className="font-display font-black text-base text-white">{h.team_name}</span>
                                <span className="font-mono text-[9px] uppercase tracking-widest text-white/40">{tla}</span>
                                {h.historical_dark_horse && (
                                    <span className="font-mono text-[8px] uppercase tracking-widest px-1 border border-fa-violet/60 text-fa-violet">Historic DH</span>
                                )}
                                {h.favourable_md1 && (
                                    <span className="font-mono text-[8px] uppercase tracking-widest px-1 border border-fa-mint/60 text-fa-mint">Easy MD1</span>
                                )}
                                {h.one_to_watch_count > 0 && (
                                    <span className="font-mono text-[8px] uppercase tracking-widest px-1 border border-fa-yellow/60 text-fa-yellow flex items-center gap-1">
                                        <Sparkle size={8} weight="fill" />
                                        {h.one_to_watch_count} OTW
                                    </span>
                                )}
                                <span className="ml-auto font-mono text-[11px] tabular-nums text-fa-violet">{h.total_score}<span className="text-white/30 text-[9px] ml-0.5">pts</span></span>
                            </div>
                            {/* Score bar */}
                            <div className="mt-1.5 h-1 bg-white/5 relative">
                                <div
                                    className="h-full bg-fa-violet/70"
                                    style={{ width: `${barPct}%` }}
                                />
                            </div>
                            {/* Top picks line */}
                            {h.top_picks && h.top_picks.length > 0 && (
                                <div className="font-mono text-[9px] uppercase tracking-widest text-white/40 mt-1.5">
                                    Top picks: {h.top_picks.slice(0, 3).map((p, i) => (
                                        <span key={i}>
                                            {i > 0 && <span className="text-white/15"> · </span>}
                                            <span className="text-white/70">{p.name}</span>
                                            <span className="text-white/30"> #{p.rank}</span>
                                            {p.fpl_team_name && <span className="text-white/20"> ({p.fpl_team_name})</span>}
                                        </span>
                                    ))}
                                </div>
                            )}
                            {h.md1_opponent && (
                                <div className="font-mono text-[9px] uppercase tracking-widest text-white/30 mt-0.5">
                                    MD1 vs {h.md1_opponent}
                                </div>
                            )}
                        </Link>
                    );
                })}
                <p className="font-mono text-[9px] uppercase tracking-widest text-white/30 pt-1">
                    Score = PL crossref × 3 + PL Top-50 × 5 + historic DH +10 + favourable MD1 +5 + FIFA &ldquo;One to Watch&rdquo; × 4 + £7.5m+ stars × 2
                </p>
            </div>
        </section>
    );
}

export default function WorldCupHub() {
    const { user } = useAuth();
    const [overview, setOverview] = useState(null);
    const [groups, setGroups] = useState([]);
    const [md1Matches, setMd1Matches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);

    const loadAll = async () => {
        setLoading(true);
        try {
            const [ov, gr, md1] = await Promise.all([
                getWcOverview(),
                getWcGroups(),
                getWcMatches({ stage: "GROUP_STAGE", matchday: 1, limit: 30 }),
            ]);
            setOverview(ov);
            setGroups(gr.groups || []);
            setMd1Matches(md1.matches || []);
        } catch (e) {
            toast.error("Failed to load World Cup data — try Sync.");
        } finally {
            setLoading(false);
        }
    };

    function handleSync() {
        return (async () => {
            toast.info("Syncing 48 teams + 104 matches — takes ~25s due to rate limits…");
            setSyncing(true);
            try {
                const r = await syncWorldCup();
                toast.success(`Synced: ${r.teams} teams · ${r.players} players · ${r.matches} matches`);
                await loadAll();
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Sync failed");
            } finally {
                setSyncing(false);
            }
        })();
    }

    useEffect(() => { loadAll(); }, []);

    const syncedAtLabel = computeSyncedAtLabel(overview?.meta?.synced_at);

    const meta = overview?.meta?.competition;
    const counts = overview?.counts || {};

    return (
        <AppShell>
            <section className="space-y-6">
                {/* Hero */}
                <header className="border-2 border-fa-yellow/40 bg-[linear-gradient(135deg,#1a0d20,#0d0d0d)] p-6 md:p-8" data-testid="wc-hero">
                    <div className="inline-flex items-center gap-2 border border-white/25 bg-white/[0.05] px-3 py-1.5 mb-3 font-mono text-[10px] uppercase tracking-widest text-white/65" data-testid="wc-archive-banner">
                        <Trophy size={11} weight="fill" className="text-fa-yellow" /> Turnaj skončil — archiv & síň slávy
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow flex items-center gap-2">
                        <Trophy size={11} weight="fill" />
                        World Cup 2026 Decision Engine
                    </div>
                    <h1 className="font-display font-black text-3xl md:text-5xl uppercase tracking-tight mt-2">
                        {meta?.name || "FIFA World Cup"}
                    </h1>
                    <p className="font-mono text-[12px] text-white/60 mt-3">
                        {meta?.start ? `${meta.start} → ${meta.end}` : "Loading window…"}
                        {counts.teams ? (
                            <> · {counts.teams} teams · {counts.players} players · {counts.matches} matches</>
                        ) : null}
                    </p>
                    {syncedAtLabel && (
                        <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 mt-1" data-testid="wc-sync-stamp">
                            Last synced {syncedAtLabel}
                            <span className="text-white/20"> · auto-sync 24h</span>
                        </p>
                    )}
                    <div className="flex items-center gap-2 mt-4 flex-wrap">
                        <Link
                            to="/worldcup/strategy"
                            className="bg-fa-yellow text-black font-display font-black uppercase tracking-wide text-sm px-5 py-2.5 hover:bg-fa-yellow/90 transition-colors"
                            data-testid="wc-strategy-link"
                        >
                            Strategy Tools →
                        </Link>
                        <Link
                            to={DEFAULT_ADMIN_MD_PATH}
                            className="bg-fa-mint text-black font-display font-black uppercase tracking-wide text-sm px-5 py-2.5 hover:bg-fa-mint/90 transition-colors"
                            data-testid="wc-matchday-link"
                        >
                            MD{ACTIVE_FROM_MD} Call · BUY/KEEP/SELL/AVOID →
                        </Link>
                        <Link
                            to="/worldcup/standings"
                            className="border border-fa-mint/60 text-fa-mint font-mono text-[11px] uppercase tracking-widest px-4 py-2 hover:bg-fa-mint/10"
                            data-testid="wc-standings-link"
                        >
                            Standings + FDR
                        </Link>
                        <Link
                            to="/worldcup/players"
                            className="border border-fa-cream/60 text-fa-cream font-mono text-[11px] uppercase tracking-widest px-4 py-2 hover:bg-fa-cream/10"
                            data-testid="wc-players-link"
                        >
                            PL value picks
                        </Link>
                        <Link
                            to={DEFAULT_MD_PATH}
                            className="bg-fa-yellow text-black font-mono text-[11px] uppercase tracking-widest px-4 py-2 hover:bg-fa-yellow/90 flex items-center gap-1.5"
                            data-testid="wc-public-play-link"
                            title="Public Round Call — share with anyone, no login needed"
                        >
                            <Sparkle size={11} weight="fill" />
                            Play Round Call
                        </Link>
                        <Link
                            to="/worldcup/dark-horses"
                            className="border border-[#F08080]/60 text-[#F08080] font-mono text-[11px] uppercase tracking-widest px-4 py-2 hover:bg-[#F08080]/10"
                            data-testid="wc-dark-horses-link"
                        >
                            Dark Horses
                        </Link>
                        {user?.is_admin && (
                            <Link
                                to="/worldcup/admin/round-call"
                                className="border-2 border-fa-yellow text-fa-yellow font-mono text-[11px] uppercase tracking-widest px-4 py-2 hover:bg-fa-yellow/10 flex items-center gap-1.5"
                                data-testid="wc-admin-link"
                                title="Admin only — curate Round Call picks"
                            >
                                <Sparkle size={11} weight="fill" />
                                Curate Round Call
                            </Link>
                        )}
                        <button
                            type="button"
                            onClick={handleSync}
                            disabled={syncing}
                            className="border border-white/15 text-white/60 font-mono text-[11px] uppercase tracking-widest px-4 py-2 hover:bg-white/5 disabled:opacity-40 flex items-center gap-1.5"
                            data-testid="wc-sync-btn"
                        >
                            <ArrowsClockwise size={11} weight="bold" />
                            {syncing ? "Syncing…" : "Re-sync data"}
                        </button>
                    </div>
                </header>

                {loading ? (
                    <div className="font-mono text-[12px] text-white/40">Loading World Cup data…</div>
                ) : (
                    <>
                        {/* Czechia widget (only renders if Czechia is in synced data) */}
                        <CzechiaWatch />

                        {/* MD1 schedule — "exploit phase" */}
                        <section data-testid="wc-md1-section">
                            <div className="flex items-baseline justify-between mb-3">
                                <div>
                                    <div className="font-mono text-[10px] uppercase tracking-widest text-fa-mint flex items-center gap-1.5">
                                        <GlobeHemisphereWest size={11} weight="fill" />
                                        Matchday 1 · exploit phase
                                    </div>
                                    <h2 className="font-display font-black text-xl md:text-2xl uppercase tracking-tight">
                                        Build your MD1 squad on fixtures, not names
                                    </h2>
                                </div>
                                <span className="font-mono text-[10px] text-white/40">
                                    {md1Matches.length} matches
                                </span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 border border-white/10 bg-[#0d0d0d] p-3" data-testid="wc-md1-list">
                                {md1Matches.map((m) => <NextMatchRow key={m.fd_match_id} match={m} />)}
                            </div>
                        </section>

                        {/* Dark Horse Index — top 5 surprise teams */}
                        <DarkHorseIndex />

                        {/* Groups */}
                        <section data-testid="wc-groups-section">
                            <div className="flex items-baseline justify-between mb-3">
                                <div className="font-mono text-[10px] uppercase tracking-widest text-fa-cream flex items-center gap-1.5">
                                    <Flag size={11} weight="fill" />
                                    12 groups · 48 teams
                                </div>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="wc-groups-grid">
                                {groups.map((g) => <GroupCard key={g.group} group={g} />)}
                            </div>
                        </section>
                    </>
                )}
            </section>
        </AppShell>
    );
}
